import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import PaymentPlanForm from './PaymentPlanForm';
import PaymentPlanDetail from './PaymentPlanDetail';
import { MOTIVOS } from './paymentPlanUtils';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * El plan de pagos de un expediente (docs/planes-de-pago-fase-1-plan.md, §7):
 * el vigente con sus cuotas, cancelarlo, «cancelar y crear otro» y el
 * histórico; o, si no hay ninguno, darlo de alta.
 *
 * Quién puede lo decide el servidor (`canManagePaymentPlan`): si contesta 403,
 * aquí se dice y no se ofrece nada.
 *
 * `onStateChange(estado)` avisa a la ficha de que el estado ha cambiado: el alta
 * pone `Plan de pagos`, y cancelar, `Cold`.
 */
const CancelForm = ({ submitting, onSubmit, onBack, label }) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  return (
    <div className='flex flex-wrap items-end gap-2 text-sm'>
      <label className='flex flex-col gap-1 text-slate-600'>
        Motivo
        <select aria-label='Motivo de la cancelación' value={reason} onChange={e => setReason(e.target.value)} className='rounded-lg border-slate-300 text-sm'>
          <option value=''>Elige…</option>
          {MOTIVOS.map(m => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
      <label className='flex flex-col gap-1 text-slate-600 flex-1 min-w-[14rem]'>
        Nota
        <input aria-label='Nota de la cancelación' value={note} onChange={e => setNote(e.target.value)} className='rounded-lg border-slate-300 text-sm' />
      </label>
      <button
        type='button'
        disabled={!reason || submitting}
        onClick={() => onSubmit({ reason, ...(note.trim() ? { note: note.trim() } : {}) })}
        className='rounded-lg bg-rose-600 px-3 py-2 text-white disabled:opacity-50'
      >
        {label}
      </button>
      <button type='button' onClick={onBack} className='rounded-lg px-3 py-2 text-slate-600 hover:bg-slate-100'>
        Volver
      </button>
    </div>
  );
};

const PaymentPlanSection = ({ expedientId, debt, onStateChange }) => {
  const [estado, setEstado] = useState({ status: 'loading', data: null });
  // 'view' | 'create' | 'cancel' | 'replace-reason' | 'replace-plan'
  const [modo, setModo] = useState('view');
  const [motivoSustitucion, setMotivoSustitucion] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/payment-plans/expedients/${expedientId}`);
      setEstado({ status: 'ok', data: data.data });
    } catch (err) {
      setEstado({ status: err?.response?.status === 403 ? 'forbidden' : 'error', data: null });
    }
  }, [expedientId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const hacer = async (fn, { ok, estadoNuevo } = {}) => {
    setEnviando(true);
    setError(null);
    try {
      await fn();
      if (ok) toast.success(ok);
      if (estadoNuevo) onStateChange?.(estadoNuevo);
      setModo('view');
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setEnviando(false);
    }
  };

  if (estado.status === 'loading') return <p className='text-sm text-slate-400'>Cargando el plan de pagos…</p>;
  if (estado.status === 'forbidden') {
    return <p className='text-sm text-slate-500'>No tienes acceso a los planes de pago de este expediente.</p>;
  }
  if (estado.status === 'error') return <p className='text-sm text-rose-700'>No se ha podido cargar el plan de pagos.</p>;

  const { active, history } = estado.data;

  return (
    <div className='space-y-5'>
      {!active && modo !== 'create' && (
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-slate-600'>Este expediente no tiene ningún plan de pagos vigente.</p>
          <button type='button' onClick={() => setModo('create')} className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white'>
            Crear plan de pagos
          </button>
        </div>
      )}

      {!active && modo === 'create' && (
        <PaymentPlanForm
          debt={debt}
          submitting={enviando}
          error={error}
          onCancel={() => setModo('view')}
          onSubmit={body =>
            hacer(() => api.post(`/payment-plans/expedients/${expedientId}`, body), {
              ok: 'Plan de pagos creado',
              estadoNuevo: 'Plan de pagos',
            })
          }
        />
      )}

      {active && (
        <div className='space-y-3'>
          <PaymentPlanDetail plan={active} />
          {modo === 'view' && (
            <div className='flex flex-wrap gap-2'>
              <button type='button' onClick={() => setModo('cancel')} className='rounded-lg border border-rose-300 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50'>
                Cancelar plan
              </button>
              <button type='button' onClick={() => setModo('replace-reason')} className='rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50'>
                Cancelar y crear otro
              </button>
            </div>
          )}
          {modo === 'cancel' && (
            <>
              <p className='text-sm text-slate-600'>
                El plan queda guardado en el histórico y el expediente pasa a <strong>{expedientStatusLabel('Cold')}</strong>.
              </p>
              <CancelForm
                label='Cancelar el plan'
                submitting={enviando}
                onBack={() => setModo('view')}
                onSubmit={body =>
                  hacer(() => api.post(`/payment-plans/${active._id}/cancel`, body), { ok: 'Plan cancelado', estadoNuevo: 'Cold' })
                }
              />
            </>
          )}
          {modo === 'replace-reason' && (
            <>
              <p className='text-sm text-slate-600'>
                Primero, por qué se cancela el actual. El expediente sigue en <strong>Plan de pagos</strong>.
              </p>
              <CancelForm
                label='Siguiente: el plan nuevo'
                submitting={false}
                onBack={() => setModo('view')}
                onSubmit={body => {
                  setMotivoSustitucion(body);
                  setModo('replace-plan');
                }}
              />
            </>
          )}
          {modo === 'replace-plan' && (
            <PaymentPlanForm
              debt={debt}
              submitting={enviando}
              error={error}
              submitLabel='Sustituir el plan'
              onCancel={() => setModo('view')}
              onSubmit={plan =>
                hacer(() => api.post(`/payment-plans/expedients/${expedientId}/replace`, { cancellation: motivoSustitucion, plan }), {
                  ok: 'Plan sustituido',
                })
              }
            />
          )}
          {error && modo !== 'replace-plan' && (
            <p role='alert' className='text-sm text-rose-700'>
              {error}
            </p>
          )}
        </div>
      )}

      {history.length > 0 && (
        <details className='border-t border-slate-100 pt-3'>
          <summary className='text-sm text-slate-600 cursor-pointer'>Planes anteriores ({history.length})</summary>
          <div className='space-y-4 mt-3'>
            {history.map(p => (
              <div key={p._id} className='border border-slate-200 rounded-lg p-3'>
                <PaymentPlanDetail plan={p} />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default PaymentPlanSection;
